
// src/pages/Credits.js
import React, { useState } from 'react';
import { Container, Card, Row, Col, Button, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from '../api/services/user';

const Credits = () => {
    const [selectedCredits, setSelectedCredits] = useState(100);
    const [paymentMethod, setPaymentMethod] = useState('card');
    const [showPaymentForm, setShowPaymentForm] = useState(false);

    const queryClient = useQueryClient();

    // Fetch user credits
    const { data: credits, isLoading, error } = useQuery({
        queryKey: ['credits'],
        queryFn: () => userService.getCredits().then(res => res.data)
        }
    );

    // Buy credits mutation
    const buyCredits = useMutation(
        {
            mutationFn:(amount) => userService.buyCredits(amount),
            onSuccess: () => {
                queryClient.invalidateQueries(['credits']);
                setShowPaymentForm(false);
            }
        }
    );

    const handleBuyCredits = () => {
        setShowPaymentForm(true);
    };

    const handlePaymentSubmit = (e) => {
        e.preventDefault();
        buyCredits.mutate(selectedCredits);
    };

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Načítání kreditů...</span>
                </Spinner>
            </div>
        );
    }

    if (error) {
        return <Alert variant="danger">Došlo k chybě při načítání kreditů.</Alert>;
    }

    return (
        <Container fluid className="">
            <h1 className="mb-4 ">Kredity</h1>

            <Row>
                <Col lg={4} className="mb-4">
                    <Card className="shadow-sm h-100">
                        <Card.Body className="p-4">
                            <div className="text-center mb-4">
                                <div className="avatar-circle bg-primary mx-auto mb-3">
                                    <i className="fas fa-coins text-white fa-2x"></i>
                                </div>
                                <h5>Aktuální zůstatek</h5>
                                <h2 className="display-4 fw-bold">{credits?.amount || 0}</h2>
                                <p className="text-muted">dostupných kreditů</p>
                            </div>

                            <div className="d-grid">
                                <Button
                                    variant="primary"
                                    size="lg"
                                    onClick={handleBuyCredits}
                                >
                                    <i className="fas fa-plus-circle me-2"></i> Dobít kredity
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={8}>
                    {showPaymentForm ? (
                        <Card className="shadow-sm mb-4">
                            <Card.Header className="bg-white">
                                <h5 className="mb-0">Nákup kreditů</h5>
                            </Card.Header>
                            <Card.Body className="p-4">
                                {buyCredits.isError && (
                                    <Alert variant="danger">
                                        Nepodařilo se provést platbu. Zkuste to prosím znovu.
                                    </Alert>
                                )}

                                {buyCredits.isSuccess && (
                                    <Alert variant="success">
                                        <i className="fas fa-check-circle me-2"></i>
                                        Platba byla úspěšně zpracována! Kredity byly připsány na váš účet.
                                    </Alert>
                                )}

                                <Form onSubmit={handlePaymentSubmit}>
                                    <Form.Group className="mb-4">
                                        <Form.Label>Vyberte množství kreditů</Form.Label>
                                        <div className="d-flex flex-wrap gap-2">
                                            {[100, 200, 500, 1000, 2000].map(amount => (
                                                <Button
                                                    key={amount}
                                                    variant={selectedCredits === amount ? 'primary' : 'outline-primary'}
                                                    onClick={() => setSelectedCredits(amount)}
                                                    className="px-4 py-2"
                                                >
                                                    {amount} kreditů
                                                </Button>
                                            ))}
                                        </div>
                                        <div className="mt-3">
                                            <h5>Celkem k úhradě: <span className="text-primary">{selectedCredits} Kč</span></h5>
                                        </div>
                                    </Form.Group>

                                    <Form.Group className="mb-4">
                                        <Form.Label>Způsob platby</Form.Label>
                                        <div className="payment-methods">
                                            <Form.Check
                                                type="radio"
                                                name="paymentMethod"
                                                id="payment-card"
                                                label={<span><i className="fas fa-credit-card me-2"></i>Platební karta</span>}
                                                checked={paymentMethod === 'card'}
                                                onChange={() => setPaymentMethod('card')}
                                                className="p-3 border rounded mb-2"
                                            />
                                            <Form.Check
                                                type="radio"
                                                name="paymentMethod"
                                                id="payment-paypal"
                                                label={<span><i className="fab fa-paypal me-2"></i>PayPal</span>}
                                                checked={paymentMethod === 'paypal'}
                                                onChange={() => setPaymentMethod('paypal')}
                                                className="p-3 border rounded mb-2"
                                            />
                                            <Form.Check
                                                type="radio"
                                                name="paymentMethod"
                                                id="payment-gopay"
                                                label={<span><i className="fas fa-wallet me-2"></i>GoPay</span>}
                                                checked={paymentMethod === 'gopay'}
                                                onChange={() => setPaymentMethod('gopay')}
                                                className="p-3 border rounded mb-2"
                                            />
                                        </div>
                                    </Form.Group>

                                    {paymentMethod === 'card' && (
                                        <div className="card-form p-4 border rounded mb-4 bg-light">
                                            <h6 className="mb-3">Údaje o platební kartě</h6>
                                            <Row>
                                                <Col xs={12} className="mb-3">
                                                    <Form.Group>
                                                        <Form.Label>Číslo karty</Form.Label>
                                                        <Form.Control type="text" placeholder="1234 5678 9012 3456" />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col xs={6} className="mb-3">
                                                    <Form.Group>
                                                        <Form.Label>Platnost</Form.Label>
                                                        <Form.Control type="text" placeholder="MM/RR" />
                                                    </Form.Group>
                                                </Col>
                                                <Col xs={6} className="mb-3">
                                                    <Form.Group>
                                                        <Form.Label>CVV</Form.Label>
                                                        <Form.Control type="text" placeholder="123" />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </div>
                                    )}

                                    <div className="d-flex justify-content-between">
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => setShowPaymentForm(false)}
                                        >
                                            <i className="fas fa-times me-2"></i> Zrušit
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            size="lg"
                                            disabled={buyCredits.isLoading}
                                        >
                                            {buyCredits.isLoading ? (
                                                <>
                                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                                    Zpracování platby...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-lock me-2"></i> Zaplatit {selectedCredits} Kč
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </Form>
                            </Card.Body>
                        </Card>
                    ) : (
                        <Card className="shadow-sm mb-4">
                            <Card.Header className="bg-white">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0">Historie transakcí</h5>
                                    <Badge bg="primary" pill>{credits?.transactions?.length || 0}</Badge>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-0">
                                {credits?.transactions?.length > 0 ? (
                                    <div className="table-responsive">
                                        <table className="table table-hover mb-0">
                                            <thead>
                                            <tr>
                                                <th>Datum</th>
                                                <th>Typ</th>
                                                <th>Popis</th>
                                                <th className="text-end">Částka</th>
                                                <th className="text-end">Zůstatek</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {(credits?.transactions || []).map((transaction, index) => (
                                                <tr key={index}>
                                                    <td>{new Date(transaction.date).toLocaleDateString()}</td>
                                                    <td>
                                                        <Badge bg={
                                                            transaction.type === 'purchase' ? 'success' :
                                                                transaction.type === 'usage' ? 'warning' :
                                                                    'info'
                                                        }>
                                                            {transaction.type === 'purchase' ? 'Nákup' :
                                                                transaction.type === 'usage' ? 'Použití' :
                                                                    'Systém'}
                                                        </Badge>
                                                    </td>
                                                    <td>{transaction.description}</td>
                                                    <td className="text-end">
                              <span className={transaction.amount > 0 ? 'text-success' : 'text-danger'}>
                                {transaction.amount > 0 ? '+' : ''}{transaction.amount}
                              </span>
                                                    </td>
                                                    <td className="text-end">{transaction.balance}</td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-center py-5">
                                        <i className="fas fa-history fa-3x text-muted mb-3"></i>
                                        <h5>Zatím nemáte žádné transakce</h5>
                                        <p className="text-muted">Začněte přidáním kreditů na váš účet</p>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    <Card className="shadow-sm">
                        <Card.Header className="bg-white">
                            <h5 className="mb-0">Co jsou kredity a jak fungují?</h5>
                        </Card.Header>
                        <Card.Body className="p-4">
                            <Row className="gx-4 gy-4">
                                <Col md={6}>
                                    <div className="d-flex">
                                        <div className="me-3">
                                            <div className="avatar-circle bg-light">
                                                <i className="fas fa-globe text-primary"></i>
                                            </div>
                                        </div>
                                        <div>
                                            <h5>Monitorování webů</h5>
                                            <p>Jeden kredit vám umožní monitorovat jeden web po dobu 24 hodin.</p>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="d-flex">
                                        <div className="me-3">
                                            <div className="avatar-circle bg-light">
                                                <i className="fas fa-shield-alt text-primary"></i>
                                            </div>
                                        </div>
                                        <div>
                                            <h5>Bezpečnostní skeny</h5>
                                            <p>Bezpečnostní sken spotřebuje 5 kreditů za jeden web.</p>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="d-flex">
                                        <div className="me-3">
                                            <div className="avatar-circle bg-light">
                                                <i className="fas fa-database text-primary"></i>
                                            </div>
                                        </div>
                                        <div>
                                            <h5>Zálohy</h5>
                                            <p>Vytvoření zálohy webu spotřebuje 10 kreditů za jednu zálohu.</p>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="d-flex">
                                        <div className="me-3">
                                            <div className="avatar-circle bg-light">
                                                <i className="fas fa-chart-line text-primary"></i>
                                            </div>
                                        </div>
                                        <div>
                                            <h5>Výkonnostní testy</h5>
                                            <p>Kompletní performance test spotřebuje 3 kredity za jeden web.</p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default Credits;